import { useState, useEffect } from "react";
import { useGlobal } from "reactn";
import { Helmet } from "react-helmet-async";
import { Link, useLocation } from "react-router-dom";
import AccountLandingPageLayout from "components/Accounts/Layouts/AccountLandingPageLayout";
import CustomInput from "components/CustomInput";
import { Checkbox } from "@ugg/shared/components/common/Checkbox";
import ButtonSuccess from "components/Buttons/ButtonSuccess";

import { useInitiateOauthLoginLazyQuery } from "@ugitgud/legos/queries/auth";
import { useLogin } from "lib/hooks/userHooks";
import { parseQueryString } from "@ugg/shared/utils/url-query-params";
import { AppRoutes } from "@ugg/shared/routes/app-routes";

import { Icon } from "@ugitgud/legos/ui/icon/Icon";
import { SvgLolDiscordIcon } from "@ugitgud/legos/ui/icon/SVG/LolDiscordIcon";
import { SvgBrandsGoogle } from "@ugitgud/legos/ui/icon/SVG/BrandsGoogle";
import { SvgBrandsRiot } from "@ugitgud/legos/ui/icon/SVG/BrandsRiot";

const Login = () => {
  const location = useLocation();
  const search = location.search.substring(1);
  const parsedQueryString = parseQueryString(search);
  const [loginError, setLoginError] = useState("");
  const [loggingIn] = useGlobal("loggingIn");
  // Form
  const [form, setForm] = useState({
    email: { value: "", error: "" },
    password: { value: "", error: "" },
    persistLogin: { value: true },
  });

  useEffect(() => {
    window?.scrollTo(0, 0);
  }, []);

  const { email, password, persistLogin } = form;

  const loginCreds = {
    persistLogin: persistLogin.value,
    email: email.value,
    password: password.value,
  };

  const onCompleted = () => {
    let redirectURL = AppRoutes.HOME;
    if (parsedQueryString.continue) {
      const splitQuery = parsedQueryString.continue.split("/");
      redirectURL = `${AppRoutes.PREMIUM_CHECKOUT}?planType=${splitQuery.pop()}`;
    }
    if (parsedQueryString.redirect_url) {
      redirectURL = parsedQueryString.redirect_url;
    }
    window.location.href = redirectURL;
  };

  const onError = (error) => {
    // Bad email or password
    if (error === "unauthorized") {
      setLoginError("Incorrect email/password");
    }
    // Other errors
    else {
      setLoginError("An unexpected error occurred. Please try again.");
    }
  };

  const login = useLogin(loginCreds, onCompleted, onError);

  const onInputChange = (key, value) => {
    setForm({ ...form, [key]: { ...form[key], ...value } });
  };

  const verifyForm = () => {
    let verifiedForm = {};
    let hasError = false;
    let foundValidationError = false;
    function mergeForm(state, error) {
      if (error === true) {
        hasError = true;
        foundValidationError = true;
      }
      return Object.assign(verifiedForm, state);
    }

    if (email.value !== undefined) {
      if (email.value.trim().length === 0) {
        verifiedForm = mergeForm({ email: { ...email, error: "Missing email" } }, true);
      } else {
        verifiedForm = mergeForm({ email: { ...email, error: "" } });
      }
    }

    // Password
    if (password.value.trim().length === 0) {
      verifiedForm = mergeForm({ password: { ...password, error: "Missing password" } }, true);
    } else {
      verifiedForm = mergeForm({ password: { ...password, error: "" } });
    }

    // Clear loginError if a validation error is found
    if (foundValidationError) {
      setLoginError("");
    }

    return { form: verifiedForm, error: hasError };
  };

  const completeLogin = (e) => {
    const verifiedForm = verifyForm();
    setForm({ ...form, ...verifiedForm.form });

    if (verifiedForm.error) return;

    setLoginError("");
    login();
  };

  const switchToSignup = () => {
    let continuePath = [];
    if (parsedQueryString.continue) {
      continuePath.push(`continue=${parsedQueryString.continue}`);
    }
    if (parsedQueryString.redirect_url) {
      continuePath.push(`redirect_url=${parsedQueryString.redirect_url}`);
    }

    return { pathname: AppRoutes.SIGNUP, search };
  };

  // Oauth Login
  const [initiateOauthLogin, oauthState] = useInitiateOauthLoginLazyQuery();

  useEffect(() => {
    if (oauthState.data?.initiateOauthLogin?.url) {
      window.location.href = oauthState.data.initiateOauthLogin.url;
    }
  }, [oauthState.data]);

  const handleOauthLogin = (providerName) => {
    initiateOauthLogin({ variables: { providerName } });
  };

  return (
    <AccountLandingPageLayout>
      <Helmet>
        <title>U.GG - Login</title>
      </Helmet>
      <div className="text-white text-[30px] font-bold font-barlow">Welcome back!</div>
      <div className="mb-[36px] text-white text-[30px] font-bold font-barlow">Log in to continue.</div>
      <form onSubmit={(e) => e.preventDefault()} className="w-full" style={{ textAlign: "left" }}>
        {loginError && <p className="_error-msg mb-[8px] text-[14px] text-accent-red-450 font-[500]">{loginError}</p>}
        <CustomInput
          className="
                    mb-[12px] [&_input]:h-[45px] [&_input]:w-full [&_input]:max-w-[300px]
                    [&_input]:text-[16px] [&_input]:text-white [&_input]:rounded-[3px] [&_input]:border-0
                    [&_input]:bg-[rgba(7,7,32,0.2)] xs:[&_input]:bg-[rgba(7,7,32,0.2)] [&_input]:pl-4 [&_input:focus]:outline-none [&_input:focus]:border-0
                    [&_input]:placeholder:text-lavender-50 [&_input]:placeholder:font-normal
                    [&_.input-label]:text-[16px] [&_.input-label]:mb-[10px] [&_.input-label]:text-lavender-50 [&_.input-label]:font-inter
                    max-sm:[&_input]:bg-purple-100
                  "
          style={{
            width: "100%",
            ...(email.error || loginError ? { border: "1px solid #ff4e50", borderRadius: "3px" } : {}),
          }}
          value={email.value}
          error={email.error}
          onChange={(e) => onInputChange("email", { value: e.target.value })}
          placeholder="Email"
          type="email"
          tabIndex={1000}
          autoFocus
        />
        <CustomInput
          className="
                    [&_input]:h-[45px] [&_input]:w-full [&_input]:max-w-[300px]
                    [&_input]:text-[16px] [&_input]:text-white [&_input]:rounded-[3px] [&_input]:border-0
                    [&_input]:bg-[rgba(7,7,32,0.2)] xs:[&_input]:bg-[rgba(7,7,32,0.2)] [&_input]:pl-4 [&_input:focus]:outline-none [&_input:focus]:border-0
                    [&_input]:placeholder:text-lavender-50 [&_input]:placeholder:font-normal
                    [&_.input-label]:text-[16px] [&_.input-label]:mb-[10px] [&_.input-label]:text-lavender-50 [&_.input-label]:font-inter
                    max-sm:[&_input]:bg-purple-100
                  "
          style={{
            width: "100%",
            ...(password.error || loginError ? { border: "1px solid #ff4e50", borderRadius: "3px" } : {}),
          }}
          value={password.value}
          error={password.error}
          onChange={(e) => onInputChange("password", { value: e.target.value })}
          placeholder="Password"
          type="password"
          tabIndex={1000}
        />
        <div className="flex items-center my-[20px]">
          <Checkbox
            checkboxId="keep-me-logged-in"
            isChecked={persistLogin.value}
            onChange={(e) => onInputChange("persistLogin", { value: e.target.checked })}
          />
          <label className="ml-[8px] text-[14px] text-white leading-normal cursor-pointer" htmlFor="keep-me-logged-in">
            Keep me logged in
          </label>
        </div>
        <ButtonSuccess
          className="btn-blue h-[42px] !text-[14px]"
          label="Login"
          isLoading={loggingIn}
          onClick={completeLogin}
          bForceSubmitOnEnter={true}
        />
        <div className="flex items-center my-[20px]">
          <div className="h-[1px] w-full max-w-[131px] bg-[#5c5c8e]" />
          <span className="mx-2 text-[10px] text-lavender-50">OR</span>
          <div className="h-[1px] w-full max-w-[131px] bg-[#5c5c8e]" />
        </div>
        <div className="mb-[20px] flex justify-start">
          <button
            type="button"
            onClick={() => handleOauthLogin("riot")}
            className="bg-[#eb0029] rounded-[4px] h-[36px] w-[92px] cursor-pointer flex items-center justify-center"
            aria-label="Login with Riot"
          >
            <Icon icon={<SvgBrandsRiot />} className="w-[20px] h-[20px]" />
          </button>
          <button
            type="button"
            onClick={() => handleOauthLogin("google")}
            className="bg-[#f7f7f7] rounded-[4px] ml-[12px] h-[36px] w-[92px] cursor-pointer flex items-center justify-center"
            aria-label="Login with Google"
          >
            <Icon icon={<SvgBrandsGoogle />} className="w-[20px] h-[20px]" />
          </button>
          <button
            type="button"
            onClick={() => handleOauthLogin("discord")}
            className="bg-[#5865F2] rounded-[4px] ml-[12px] h-[36px] w-[92px] cursor-pointer flex items-center justify-center"
            aria-label="Login with Discord"
          >
            <Icon icon={<SvgLolDiscordIcon />} className="w-[20px] h-[20px]" />
          </button>
        </div>
        <div className="mb-[6px] text-lavender-50 text-[14px] font-inter">
          Don't have an account?{" "}
          <Link className="underline text-accent-blue-400 font-bold" to={switchToSignup()}>
            <span>Join now</span>
          </Link>
        </div>
        <Link to={AppRoutes.RECOVER_ACCOUNT} className="text-[14px] text-accent-blue-400 font-bold underline">
          Trouble logging in?
        </Link>
      </form>
    </AccountLandingPageLayout>
  );
};

export default Login;
